import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { queryService } from "../services/queryService";
import HelpButton from "../components/HelpButton";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  ChevronUp,
  Calendar,
  Activity,
} from "lucide-react";

function ActivityHistory() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0,
  });

  useEffect(() => {
    loadActivities(1);
  }, []);

  const loadActivities = async (page = 1, perPage = 15) => {
    try {
      setLoading(true);
      const response = await queryService.getRecentActivity(page, perPage);
      if (response.success) {
        if (response.data.data) {
          // Paginated response
          setActivities(response.data.data);
          setPagination({
            current_page: response.data.current_page,
            per_page: response.data.per_page,
            total: response.data.total,
            last_page: response.data.last_page,
            from: response.data.from,
            to: response.data.to,
          });
        } else {
          // Non-paginated response (fallback)
          setActivities(response.data);
        }
      }
    } catch (error) {
      console.error("Failed to load activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      loadActivities(page, pagination.per_page);
      setExpandedActivity(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderPaginationButtons = () => {
    const { current_page, last_page } = pagination;
    const buttons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, current_page - Math.floor(maxButtons / 2));
    let endPage = Math.min(last_page, startPage + maxButtons - 1);

    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            i === current_page
              ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg"
              : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
          }`}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-300 pb-32">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-white/20 dark:border-gray-800 shadow-lg">
        <div className="px-6 py-4 safe-top">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all active:scale-95 border border-gray-100 dark:border-gray-700"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Activity className="w-6 h-6 text-indigo-500" />
                Activity History
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {pagination.total > 0
                  ? `Showing ${pagination.from}-${pagination.to} of ${pagination.total} activities`
                  : "No activities found"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-6">
        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-md border border-gray-100 dark:border-gray-700 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading activities...</p>
            </div>
          </div>
        ) : activities.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-12 shadow-md border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center">
            <Calendar className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-center">
              No activity history found
            </p>
          </div>
        ) : (
          <>
            {/* Activities List */}
            <div className="space-y-2">
              {activities.map((activity, index) => (
                <div
                  key={`${activity.reference_id}-${index}`}
                  onClick={() =>
                    setExpandedActivity(expandedActivity === index ? null : index)
                  }
                  className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-2 h-2 ${activity.color} rounded-full animate-pulse flex-shrink-0`}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {activity.action}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 truncate">
                        {activity.description}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {activity.time_ago}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {expandedActivity === index ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedActivity === index && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1.5">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Type
                          </p>
                          <p className="text-xs font-medium text-gray-900 dark:text-white capitalize mt-0.5">
                            {activity.type?.replace(/_/g, " ")}
                          </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            App ID
                          </p>
                          <p className="text-xs font-medium text-gray-900 dark:text-white mt-0.5">
                            {activity.application_id}
                          </p>
                        </div>
                      </div>

                      {activity.reference_id &&
                        activity.reference_id !== activity.application_id && (
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Reference ID
                            </p>
                            <p className="text-xs font-medium text-gray-900 dark:text-white mt-0.5">
                              {activity.reference_id}
                            </p>
                          </div>
                        )}

                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Timestamp
                        </p>
                        <p className="text-xs font-medium text-gray-900 dark:text-white mt-0.5">
                          {new Date(activity.time).toLocaleString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {pagination.last_page > 1 && (
              <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md border border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between gap-2">
                  {/* First & Previous */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.current_page === 1}
                      className="p-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                    >
                      <ChevronsLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={pagination.current_page === 1}
                      className="p-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </button>
                  </div>

                  {/* Page Numbers */}
                  <div className="flex gap-2 overflow-x-auto">
                    {renderPaginationButtons()}
                  </div>

                  {/* Next & Last */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={pagination.current_page === pagination.last_page}
                      className="p-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                    >
                      <ChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.last_page)}
                      disabled={pagination.current_page === pagination.last_page}
                      className="p-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                    >
                      <ChevronsRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Help Button */}
      <HelpButton />
    </div>
  );
}

export default ActivityHistory;
